import { useConfirmDialog } from '../hooks/useConfirmDialog';
import React, { useEffect, useState } from 'react';
import { getErrorMessage, formatCurrency } from '@/lib/utils';
import { useI18n } from '../contexts/I18nContext';
import { useExtensions } from '../hooks/useExtensions';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  MapPin, 
  Plus, 
  Edit2, 
  Trash2, 
  Clock,
  Euro,
  Truck,
  Save,
  Loader2,
  Settings,
  Lock,
  ArrowLeft
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Giorni della settimana
const WEEKDAYS = [
  { value: 0, label: 'Lunedì' },
  { value: 1, label: 'Martedì' },
  { value: 2, label: 'Mercoledì' },
  { value: 3, label: 'Giovedì' },
  { value: 4, label: 'Venerdì' },
  { value: 5, label: 'Sabato' },
  { value: 6, label: 'Domenica' }
];

const DeliveryZonesPage = () => {
  const { confirm: confirmAction, ConfirmDialog } = useConfirmDialog();
  const { t } = useI18n();
  const navigate = useNavigate();
  const { isFeatureActive } = useExtensions();
  
  const [settings, setSettings] = useState(null);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Dialog zona
  const [showZoneDialog, setShowZoneDialog] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [zoneForm, setZoneForm] = useState({
    name: '',
    zone_type: 'cap',
    postal_codes: '',
    provinces: '',
    base_delivery_cost: 0,
    free_delivery_threshold: '',
    min_delivery_days: 1,
    max_delivery_days: 3,
    time_slots: [],
    is_active: true,
    priority: 0,
    public_notes: ''
  });
  const [savingZone, setSavingZone] = useState(false);

  // Verifica feature attiva
  const hasDeliveryFeature = isFeatureActive('ext_logistics', 'delivery');

  useEffect(() => {
    if (hasDeliveryFeature) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [hasDeliveryFeature]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [settingsRes, zonesRes] = await Promise.all([
        axios.get(`${API}/delivery-zones/settings`),
        axios.get(`${API}/delivery-zones/zones`)
      ]);
      setSettings(settingsRes.data);
      setZones(zonesRes.data.zones || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/delivery-zones/settings`, settings);
      toast.success('Impostazioni salvate');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nel salvataggio'));
    } finally {
      setSavingSettings(false);
    }
  };

  const handleOpenZoneDialog = (zone = null) => {
    if (zone) {
      setEditingZone(zone);
      setZoneForm({
        name: zone.name || '',
        zone_type: zone.zone_type || 'cap',
        postal_codes: (zone.postal_codes || []).join(', '),
        provinces: (zone.provinces || []).join(', '),
        base_delivery_cost: zone.base_delivery_cost || 0,
        free_delivery_threshold: zone.free_delivery_threshold || '',
        min_delivery_days: zone.min_delivery_days || 1,
        max_delivery_days: zone.max_delivery_days || 3,
        time_slots: zone.time_slots || [],
        is_active: zone.is_active !== false,
        priority: zone.priority || 0,
        public_notes: zone.public_notes || ''
      });
    } else {
      setEditingZone(null);
      setZoneForm({
        name: '',
        zone_type: 'cap',
        postal_codes: '',
        provinces: '',
        base_delivery_cost: 0,
        free_delivery_threshold: '',
        min_delivery_days: 1,
        max_delivery_days: 3,
        time_slots: settings?.default_time_slots || [],
        is_active: true,
        priority: 0,
        public_notes: ''
      });
    }
    setShowZoneDialog(true);
  };

  const handleSaveZone = async () => {
    if (!zoneForm.name) {
      toast.error('Inserisci un nome per la zona');
      return;
    }

    setSavingZone(true);
    try {
      const data = {
        ...zoneForm,
        postal_codes: zoneForm.postal_codes ? zoneForm.postal_codes.split(',').map(s => s.trim()).filter(Boolean) : [],
        provinces: zoneForm.provinces ? zoneForm.provinces.split(',').map(s => s.trim().toUpperCase()).filter(Boolean) : [],
        free_delivery_threshold: zoneForm.free_delivery_threshold ? parseFloat(zoneForm.free_delivery_threshold) : null
      };

      if (editingZone) {
        await axios.put(`${API}/delivery-zones/zones/${editingZone.id}`, data);
        toast.success('Zona aggiornata');
      } else {
        await axios.post(`${API}/delivery-zones/zones`, data);
        toast.success('Zona creata');
      }
      
      setShowZoneDialog(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Errore nel salvataggio'));
    } finally {
      setSavingZone(false);
    }
  };

  const handleDeleteZone = async (zoneId) => {
    if (!await confirmAction({ title: 'Elimina zona', description: 'Eliminare questa zona di consegna?', confirmText: 'Elimina', variant: 'destructive' })) return;
    
    try {
      await axios.delete(`${API}/delivery-zones/zones/${zoneId}`);
      toast.success('Zona eliminata');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, "Errore nell'eliminazione"));
    }
  };

  const toggleDeliveryDay = (dayValue) => {
    const currentDays = settings?.delivery_days || [];
    const newDays = currentDays.includes(dayValue)
      ? currentDays.filter(d => d !== dayValue)
      : [...currentDays, dayValue].sort();
    setSettings({ ...settings, delivery_days: newDays });
  };

  // Se la feature non è attiva, mostra messaggio
  if (!hasDeliveryFeature) {
    return (
      <div className="space-y-6" data-testid="delivery-zones-page">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
              <MapPin className="h-8 w-8 text-primary" />
              Zone di Consegna
            </h1>
          </div>
        </div>

        <Card>
          <CardContent className="py-16 text-center">
            <Lock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">Feature non attiva</h3>
            <p className="text-muted-foreground mt-2 max-w-md mx-auto">
              Per gestire le zone di consegna, attiva la feature "Gestione Consegne" 
              nella sezione Estensioni delle Impostazioni.
            </p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => navigate('/settings')}
            >
              Vai alle Impostazioni
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="delivery-zones-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
              <MapPin className="h-8 w-8 text-primary" />
              Zone di Consegna
            </h1>
            <p className="text-muted-foreground mt-1">
              Configura aree, costi e fasce orarie di consegna
            </p>
          </div>
        </div>
        <Button onClick={() => handleOpenZoneDialog()}>
          <Plus className="h-4 w-4 mr-2" />
          Nuova Zona
        </Button>
      </div>

      {/* Impostazioni Generali */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Impostazioni Generali
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Toggle consegna/ritiro */}
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">Consegna a domicilio</div>
              <div className="text-sm text-muted-foreground">Abilita le consegne per i tuoi clienti</div>
            </div>
            <Switch
              checked={settings?.delivery_enabled}
              onCheckedChange={(v) => setSettings({ ...settings, delivery_enabled: v })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">Ritiro in sede</div>
              <div className="text-sm text-muted-foreground">Permetti il ritiro presso la tua sede</div>
            </div>
            <Switch
              checked={settings?.pickup_enabled}
              onCheckedChange={(v) => setSettings({ ...settings, pickup_enabled: v })}
            />
          </div>

          <Separator />

          {/* Giorni di consegna */}
          <div className="space-y-3">
            <Label>Giorni di consegna</Label>
            <div className="flex flex-wrap gap-2">
              {WEEKDAYS.map(day => (
                <Badge
                  key={day.value}
                  variant={settings?.delivery_days?.includes(day.value) ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => toggleDeliveryDay(day.value)}
                >
                  {day.label}
                </Badge>
              ))}
            </div>
          </div>

          {/* Orario limite e lead time */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Orario limite ordini</Label>
              <Input
                type="time"
                value={settings?.cutoff_time || '18:00'}
                onChange={(e) => setSettings({ ...settings, cutoff_time: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Ordini dopo questo orario slittano al giorno successivo
              </p>
            </div>
            <div className="space-y-2">
              <Label>Anticipo minimo (ore)</Label>
              <Input
                type="number"
                value={settings?.min_order_lead_time || 24}
                onChange={(e) => setSettings({ ...settings, min_order_lead_time: parseInt(e.target.value) })}
              />
              <p className="text-xs text-muted-foreground">
                Ore minime di anticipo per l'ordine
              </p>
            </div>
          </div>

          {/* Costi default */}
          <Separator />
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Costo consegna default (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={settings?.default_delivery_cost || 0}
                onChange={(e) => setSettings({ ...settings, default_delivery_cost: parseFloat(e.target.value) })}
              />
            </div>
            <div className="space-y-2">
              <Label>Soglia consegna gratuita (€)</Label>
              <Input
                type="number"
                step="0.01"
                placeholder="Nessuna soglia"
                value={settings?.default_free_threshold || ''}
                onChange={(e) => setSettings({ ...settings, default_free_threshold: e.target.value ? parseFloat(e.target.value) : null })}
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSaveSettings} disabled={savingSettings}>
              {savingSettings ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Salva Impostazioni
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Zone di Consegna */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Truck className="h-5 w-5" />
            Zone di Consegna
          </CardTitle>
          <CardDescription>
            Definisci aree geografiche con costi e tempi differenziati
          </CardDescription>
        </CardHeader>
        <CardContent>
          {zones.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MapPin className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nessuna zona configurata</p>
              <p className="text-sm">Crea la prima zona di consegna</p>
              <Button variant="outline" className="mt-4" onClick={() => handleOpenZoneDialog()}>
                <Plus className="h-4 w-4 mr-2" />
                Crea Zona
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="uppercase text-xs">Nome</TableHead>
                  <TableHead className="uppercase text-xs">Tipo</TableHead>
                  <TableHead className="uppercase text-xs">Copertura</TableHead>
                  <TableHead className="uppercase text-xs text-right">Costo</TableHead>
                  <TableHead className="uppercase text-xs text-right">Gratuita da</TableHead>
                  <TableHead className="uppercase text-xs text-center">Stato</TableHead>
                  <TableHead className="uppercase text-xs">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {zones.map(zone => (
                  <TableRow key={zone.id}>
                    <TableCell className="font-medium">{zone.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {zone.zone_type === 'cap' ? 'CAP' : 
                         zone.zone_type === 'province' ? 'Province' : 
                         zone.zone_type === 'region' ? 'Regioni' : 'Custom'}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">
                      {zone.zone_type === 'cap' && zone.postal_codes?.join(', ')}
                      {zone.zone_type === 'province' && zone.provinces?.join(', ')}
                      {zone.zone_type === 'custom' && 'Copertura generale'}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(zone.base_delivery_cost || 0)}
                    </TableCell>
                    <TableCell className="text-right">
                      {zone.free_delivery_threshold ? formatCurrency(zone.free_delivery_threshold) : '-'}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant={zone.is_active ? 'default' : 'secondary'}>
                        {zone.is_active ? 'Attiva' : 'Inattiva'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenZoneDialog(zone)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteZone(zone.id)} className="text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog Zona */}
      <Dialog open={showZoneDialog} onOpenChange={setShowZoneDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingZone ? 'Modifica Zona' : 'Nuova Zona di Consegna'}
            </DialogTitle>
            <DialogDescription>
              Configura l'area geografica e i costi di consegna
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Nome e Tipo */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nome Zona *</Label>
                <Input
                  placeholder="Es: Milano Centro"
                  value={zoneForm.name}
                  onChange={(e) => setZoneForm({ ...zoneForm, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Tipo Zona</Label>
                <Select 
                  value={zoneForm.zone_type} 
                  onValueChange={(v) => setZoneForm({ ...zoneForm, zone_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cap">Per CAP</SelectItem>
                    <SelectItem value="province">Per Province</SelectItem>
                    <SelectItem value="custom">Custom (tutto)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Copertura */}
            {zoneForm.zone_type === 'cap' && (
              <div className="space-y-2">
                <Label>CAP coperti (separati da virgola)</Label>
                <Input
                  placeholder="Es: 20121, 20122, 20123"
                  value={zoneForm.postal_codes}
                  onChange={(e) => setZoneForm({ ...zoneForm, postal_codes: e.target.value })}
                />
              </div>
            )}

            {zoneForm.zone_type === 'province' && (
              <div className="space-y-2">
                <Label>Province coperte (sigle separate da virgola)</Label>
                <Input
                  placeholder="Es: MI, MB, VA, CO"
                  value={zoneForm.provinces}
                  onChange={(e) => setZoneForm({ ...zoneForm, provinces: e.target.value })}
                />
              </div>
            )}

            <Separator />

            {/* Costi */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Euro className="h-4 w-4" />
                  Costo Consegna (€)
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={zoneForm.base_delivery_cost}
                  onChange={(e) => setZoneForm({ ...zoneForm, base_delivery_cost: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Gratuita sopra (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Nessuna soglia"
                  value={zoneForm.free_delivery_threshold}
                  onChange={(e) => setZoneForm({ ...zoneForm, free_delivery_threshold: e.target.value })}
                />
              </div>
            </div>

            {/* Tempi */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Giorni minimi consegna</Label>
                <Input
                  type="number"
                  min="0"
                  value={zoneForm.min_delivery_days}
                  onChange={(e) => setZoneForm({ ...zoneForm, min_delivery_days: parseInt(e.target.value) || 1 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Giorni massimi consegna</Label>
                <Input
                  type="number"
                  min="1"
                  value={zoneForm.max_delivery_days}
                  onChange={(e) => setZoneForm({ ...zoneForm, max_delivery_days: parseInt(e.target.value) || 3 })}
                />
              </div>
            </div>

            <Separator />

            {/* Priorità e stato */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priorità (più alto = prima)</Label>
                <Input
                  type="number"
                  value={zoneForm.priority}
                  onChange={(e) => setZoneForm({ ...zoneForm, priority: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Stato</Label>
                <div className="flex items-center gap-2 h-10">
                  <Switch
                    checked={zoneForm.is_active}
                    onCheckedChange={(v) => setZoneForm({ ...zoneForm, is_active: v })}
                  />
                  <span className="text-sm">{zoneForm.is_active ? 'Attiva' : 'Inattiva'}</span>
                </div>
              </div>
            </div>

            {/* Note pubbliche */}
            <div className="space-y-2">
              <Label>Messaggio per i clienti</Label>
              <Input
                placeholder="Es: Consegna entro le 12:00 del giorno successivo"
                value={zoneForm.public_notes}
                onChange={(e) => setZoneForm({ ...zoneForm, public_notes: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowZoneDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleSaveZone} disabled={savingZone}>
              {savingZone ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              {editingZone ? 'Aggiorna' : 'Crea'} Zona
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeliveryZonesPage;
